// Too much files to change
/* eslint-disable import/no-internal-modules */
import axios from 'axios'
import classnames from 'classnames'
import { Component } from 'preact'
import { z } from 'zod'
import { ProductAreaType } from '../../../../control-components/product-areas/ProductAreaSelect'
import TopicReplyCount from '../../../../control-components/ui-components/TopicReplyCount'
import TopicViewsCount from '../../../../control-components/ui-components/TopicViewsCount'
import { LegacyCategoryType } from '../../../../server-middleware/categoryRepository'
import { GetServerSideProps } from '../../../../server-middleware/context'
import getServerData from '../../../../services/featuredTopicsData'
import {
  FeaturedIdea,
  FeaturedProductUpdate,
  FeaturedTopicFromUnified
} from '../../../../services/unified'
import { EventListItemView } from '../../../../preact-components/Event/EventListOverview'
import { CardWidgetText } from './CardWidgetText'
import FeaturedCard from './FeaturedCard'
import FeaturedCardContainer, { isFullWidth } from './FeaturedCardContainer'
import FeaturedProductAreas from './FeaturedProductAreas'
import IdeationStatusView from './IdeationStatusView'
import { PostMetaContainer } from './PostMetaContainer'
import ShowMoreButton from './ShowMoreButton'
import {
  AnyFeaturedTopic,
  ContentType,
  FeaturedEvent,
  FeaturedTopic,
  isEvent,
  isLegacyFeaturedTopic,
  isUnified
} from './types'
import { VoteButton } from './VoteButton'
import WidgetContainer, {
  widgetContainerConfig
} from '../../../../preact-components/widget-container'
import { getInsidedData } from '@insided/destination/utils/insidedDataStorage'

const extractContentType = (topic: AnyFeaturedTopic): ContentType => {
  if (isLegacyFeaturedTopic(topic) && topic.type === LegacyCategoryType.ideation) {
    return 'idea'
  }
  return topic.contentType
}

interface FeaturedTopicsWidgetProps {
  title?: string
  config: FeaturedTopicsConfig
  featuredTopics?: AnyFeaturedTopic[]
  allProductAreas?: ProductAreaType[]
  widgetName?: string
}

interface State {
  featuredTopics: AnyFeaturedTopic[]
}

export const widgetName = 'featured_topics'

export const config = z
  .object({
    topics: z.array(
      z.object({
        id: z.number(),
        type: z.string()
      })
    ),
    buttonURL: z.string(),
    buttonLabel: z.string(),
    firstTopicsNumber: z.number()
  })
  .merge(widgetContainerConfig)

export type FeaturedTopicsConfig = z.infer<typeof config>

// prettier-ignore
export const getServerSideProps: GetServerSideProps<FeaturedTopicsConfig, FeaturedTopicsWidgetProps> = async (context, config) => {
  return {
    featuredTopics: await getServerData(context, config.topics),
    config: config
  }
}

export default class FeaturedTopicsWidget extends Component<FeaturedTopicsWidgetProps, State> {
  constructor(props: FeaturedTopicsWidgetProps) {
    super(props)

    this.state = {
      featuredTopics: props.featuredTopics ?? []
    }

    this.addLikeHandler = this.addLikeHandler.bind(this)
    this.addVoteHandler = this.addVoteHandler.bind(this)
  }

  get totalTopics() {
    return this.props.featuredTopics?.length ?? 0
  }

  get topicsFullWidth() {
    return this.props.config.firstTopicsNumber ?? 0
  }

  async addLikeHandler(index: number) {
    if (typeof window === 'undefined') return
    const { userid: userId } = getInsidedData('user')
    if (!userId) {
      const event = new window.CustomEvent('GuestUserAction')
      return window.dispatchEvent(event)
    }

    const newFeaturedTopics = [...this.state.featuredTopics]
    const topic = newFeaturedTopics[index]

    if (isEvent(topic)) return

    if (isUnified(topic)) {
      const currentUserLiked = topic.likeSet.some((id) => id === parseInt(userId))
      const action = currentUserLiked ? 'unlike' : 'like'

      await axios.post(`/community/api/${topic.contentType}s/${action}`)

      if (currentUserLiked) {
        topic.likeSet = topic.likeSet.filter((id) => id !== parseInt(userId))
      } else {
        topic.likeSet.push(parseInt(userId))
      }
      this.setState({ featuredTopics: newFeaturedTopics })
      return
    }

    let { post, id, likes, hasCurrentUserLiked: liked } = topic

    topic.likes = liked ? --likes : ++likes
    topic.hasCurrentUserLiked = !liked

    this.setState({
      featuredTopics: newFeaturedTopics
    })

    const topicHasCurrentUserLiked: string = String(topic.hasCurrentUserLiked)
    const url = `/member/like?like=${topicHasCurrentUserLiked}&tid=${id}`

    const newData = {
      liked: topic.hasCurrentUserLiked,
      postid: post?.id
    }

    return await axios.post(url, { ...newData })
  }

  async addVoteHandler(index: number, hasCurrentUserVoted: boolean) {
    if (typeof window === 'undefined') return
    if (!getInsidedData('user').userid) {
      const event = new window.CustomEvent('GuestUserAction')
      return window.dispatchEvent(event)
    }

    const newFeaturedTopics = [...this.state.featuredTopics]
    const topic = newFeaturedTopics[index] as FeaturedIdea
    let { id, voteCount } = topic

    topic.voteCount = hasCurrentUserVoted ? --voteCount : ++voteCount
    topic.hasCurrentUserVoted = !hasCurrentUserVoted

    this.setState({
      featuredTopics: newFeaturedTopics
    })

    if (hasCurrentUserVoted) {
      return await axios.post(`/community/api/ideas/${id}/unvote`, {
        unvotedBy: getInsidedData('user').userid
      })
    } else {
      return await axios.post(`/community/api/ideas/${id}/vote`, {
        votedBy: getInsidedData('user').userid
      })
    }
  }

  renderButton(featuredTopic: AnyFeaturedTopic, index: number) {
    if (typeof window === 'undefined') return
    if (featuredTopic.contentType === 'event') {
      return null
    }
    if (featuredTopic.contentType === 'idea') {
      const user = getInsidedData('user')
      const { hasCurrentUserVoted, voteCount, votes } = featuredTopic
      const hasVoted = hasCurrentUserVoted ?? (user && votes.includes(parseInt(user.userid)))
      return (
        <VoteButton
          hasVoted={hasVoted}
          votes={voteCount ?? votes.length ?? 0}
          onClick={() => {
            this.addVoteHandler(index, hasVoted)
          }}
        />
      )
    }
    if ('post' in featuredTopic && featuredTopic.type === LegacyCategoryType.ideation) {
      const { likes, hasCurrentUserLiked } = featuredTopic
      return (
        <VoteButton
          hasVoted={hasCurrentUserLiked}
          votes={likes}
          onClick={() => {
            this.addLikeHandler(index)
          }}
        />
      )
    }
    return null
  }

  renderItem(featuredTopic: AnyFeaturedTopic, index: number) {
    if (typeof window === 'undefined') return
    if (featuredTopic.contentType === 'event') {
      return this.renderEvent(featuredTopic, index)
    }
    if (featuredTopic.contentType === 'idea') {
      return this.renderIdea(featuredTopic, index)
    }
    if (featuredTopic.contentType === 'productUpdate') {
      return this.renderProductUpdate(featuredTopic, index)
    }
    if ('post' in featuredTopic && featuredTopic.type === LegacyCategoryType.ideation) {
      return this.renderLegacyTopicIdea(featuredTopic, index)
    }
    if ('post' in featuredTopic) {
      return this.renderLegacyTopic(featuredTopic, index)
    }

    return this.renderTopicFromUnified(featuredTopic, index)
  }

  renderEvent(featuredEvent: FeaturedEvent, index: number) {
    const {
      title,
      url,
      startsAt,
      endsAt,
      image,
      type,
      location,
      attendeeCount,
      attendees,
      isLoggedInUserAttendee
    } = featuredEvent

    const fullWidth = isFullWidth({
      index,
      topicsFullWidth: this.topicsFullWidth,
      totalTopics: this.totalTopics
    })
    return (
      <EventListItemView
        title={title}
        startAt={startsAt}
        endAt={endsAt}
        destinationUrl={url}
        image={image}
        type={type}
        location={location}
        isFullWidth={fullWidth}
        isFeatured
        attendees={attendees}
        attendeeCount={attendeeCount}
        isLoggedInUserAttendee={isLoggedInUserAttendee}
      />
    )
  }

  renderProductUpdate(topic: FeaturedProductUpdate, index: number) {
    const {
      content,
      label,
      title,
      url,
      featuredImage,
      replyCount,
      views,
      author,
      publishedAt,
      productAreas
    } = topic
    return (
      <FeaturedCard
        featuredImage={featuredImage}
        featuredImageAlt={`Featured Product Update image ${index + 1}`}
        title={title}
        url={url}
        label={label}
      >
        {this.renderContent(content, featuredImage, index)}
        {this.renderAreas(productAreas, index)}
        <PostMetaContainer author={author} timestamp={publishedAt}>
          <TopicViewsCount views={views} />
          <TopicReplyCount replyCount={replyCount} />
        </PostMetaContainer>
      </FeaturedCard>
    )
  }

  // TODO: SUC-193 Old ideation module is not supported when middleware module is enabled since unified show it's type as discussion
  renderTopicFromUnified(featuredTopic: FeaturedTopicFromUnified, index: number) {
    const { title, featuredImage, label, createdAt, replyCount, views, url, content, author } =
      featuredTopic

    return (
      <FeaturedCard
        featuredImage={featuredImage}
        featuredImageAlt={`Featured Topic image ${index + 1}`}
        title={title}
        url={url}
        label={label}
      >
        {this.renderContent(content, featuredImage, index)}
        <PostMetaContainer author={author} timestamp={createdAt}>
          <TopicViewsCount views={views} />
          <TopicReplyCount replyCount={replyCount} />
        </PostMetaContainer>
      </FeaturedCard>
    )
  }

  renderLegacyTopicIdea(featuredTopic: FeaturedTopic, index: number) {
    const { title, featuredImage, label, url, post, ideationStatus } = featuredTopic
    return (
      <FeaturedCard
        title={title}
        url={url}
        label={label}
        featuredImage={featuredImage}
        featuredImageAlt={`Featured Idea image ${index + 1}`}
        showLabel={false}
        titleSlot={ideationStatus && <IdeationStatusView ideationStatus={ideationStatus} />}
      >
        {this.renderContent(post.content, featuredImage, index)}
        <PostMetaContainer author={post.author} timestamp={post.creationDate} />
      </FeaturedCard>
    )
  }

  renderLegacyTopic(featuredTopic: FeaturedTopic, index: number) {
    const { title, featuredImage, label, replyCount, views, url, post } = featuredTopic

    return (
      <FeaturedCard
        featuredImage={featuredImage}
        featuredImageAlt={`Featured Idea image ${index + 1}`}
        title={title}
        url={url}
        label={label}
      >
        {this.renderContent(post.content, featuredImage, index)}
        <PostMetaContainer author={post.author} timestamp={post.creationDate}>
          <TopicViewsCount views={views} />
          <TopicReplyCount replyCount={replyCount} />
        </PostMetaContainer>
      </FeaturedCard>
    )
  }

  // TODO: SUC-193 Using internal api with publicId endpoint returns different result than calling with specific topic type and private id so this function wont work until specific idea enpoint is used
  renderIdea(topic: FeaturedIdea, index: number) {
    const {
      content,
      label,
      title,
      featuredImage,
      url,
      author,
      createdAt,
      ideationStatus,
      productAreas
    } = topic
    return (
      <FeaturedCard
        title={title}
        url={url}
        featuredImage={featuredImage}
        featuredImageAlt={`Featured Idea image ${index + 1}`}
        label={label}
        showLabel={false}
        titleSlot={ideationStatus && <IdeationStatusView ideationStatus={ideationStatus} />}
      >
        {this.renderContent(content, featuredImage, index)}
        {this.renderAreas(productAreas, index)}
        <PostMetaContainer author={author} timestamp={createdAt} />
      </FeaturedCard>
    )
  }

  renderContent(content: string, featuredImage: string, index: number) {
    if (typeof window === 'undefined') return
    const fullWidth = isFullWidth({
      index,
      topicsFullWidth: this.topicsFullWidth,
      totalTopics: this.totalTopics
    })
    const isNotMobile = getInsidedData('device')
    if ((isNotMobile && fullWidth) || !featuredImage) {
      return <CardWidgetText html={content} />
    }
    return null
  }

  renderAreas(assignedProductAreas: string[], index: number) {
    const { allProductAreas = [] } = this.props
    const fullWidth = isFullWidth({
      index,
      topicsFullWidth: this.topicsFullWidth,
      totalTopics: this.totalTopics
    })
    if (assignedProductAreas.length === 0) {
      return null
    }
    return (
      <FeaturedProductAreas
        fullWidth={fullWidth}
        allProductAreas={allProductAreas}
        assignedProductAreas={assignedProductAreas}
      />
    )
  }

  render() {
    const { config, widgetName = 'featured-topics' } = this.props

    const { buttonLabel, buttonURL } = config
    const { featuredTopics } = this.state
    if (!featuredTopics.length) {
      return null
    }

    return (
      <WidgetContainer config={config} widgetName={widgetName}>
        <div
          className={classnames('featured-topics__list', {
            'featured-topics__list--no-slider': featuredTopics.length === 1
          })}
        >
          {featuredTopics.map((featuredTopic, index) => (
            <FeaturedCardContainer
              key={index}
              topicsFullWidth={this.topicsFullWidth}
              totalTopics={featuredTopics.length}
              index={index}
              contentType={extractContentType(featuredTopic)}
              featuredImage={
                isEvent(featuredTopic) ? featuredTopic.image : featuredTopic.featuredImage
              }
            >
              {this.renderButton(featuredTopic, index)}
              <a
                href={featuredTopic.url}
                className='featured-topic__url-link'
                title={featuredTopic.title}
                aria-label={featuredTopic.title}
              />
              {this.renderItem(featuredTopic, index)}
            </FeaturedCardContainer>
          ))}
        </div>
        {buttonLabel && buttonURL && <ShowMoreButton label={buttonLabel} href={buttonURL} />}
      </WidgetContainer>
    )
  }
}
